// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each function does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/system-settings-management.md
/**
 * System Settings API Service
 * 
 * RESPONSIBILITIES:
 * - Handle all API calls related to system settings
 * - Centralized settings management
 * - Error handling for settings operations
 */

import { apiClient, API_ENDPOINTS } from '../config/api';

const systemSettingsService = {
  /**
   * Get all setting categories with counts
   */
  getCategories: async () => {
    const response = await apiClient.get(API_ENDPOINTS.SETTINGS_CATEGORIES);
    return response.data;
  },

  /**
   * Get all settings in a specific category
   */
  getByCategory: async (category) => {
    const response = await apiClient.get(`${API_ENDPOINTS.SETTINGS}/category/${category}`);
    return response.data;
  },

  /**
   * Get a specific setting by key
   */
  getByKey: async (settingKey, category = null) => {
    const params = category ? { category } : {};
    const response = await apiClient.get(`${API_ENDPOINTS.SETTINGS}/${settingKey}`, { params });
    return response.data;
  },

  /**
   * Get all settings (optionally filtered)
   */
  getAll: async (category = null, activeOnly = true) => {
    const params = { active_only: activeOnly };
    if (category) params.category = category;
    const response = await apiClient.get(API_ENDPOINTS.SETTINGS, { params });
    return response.data;
  },

  /**
   * Create a new setting
   * Requires: Super Admin permission
   */
  create: async (settingData) => {
    const response = await apiClient.post(API_ENDPOINTS.SETTINGS, settingData);
    return response.data;
  },

  /**
   * Update an existing setting
   * Requires: Super Admin permission
   */
  update: async (settingId, settingData) => {
    const response = await apiClient.put(`${API_ENDPOINTS.SETTINGS}/${settingId}`, settingData);
    return response.data;
  },

  /**
   * Reset a voice_processing setting to seed default (POST).
   */
  restoreVoiceProcessingDefault: async (settingId) => {
    const response = await apiClient.post(
      `${API_ENDPOINTS.SETTINGS}/${settingId}/restore-voice-default`,
    );
    return response.data;
  },

  /**
   * Delete a setting (only if not system setting)
   * Requires: Super Admin permission
   */
  delete: async (settingId) => {
    await apiClient.delete(`${API_ENDPOINTS.SETTINGS}/${settingId}`);
  },

  /**
   * Add an option to an array-type setting.
   * @param {Object} setting - Full setting object (from getByCategory) with id and setting_value
   * @param {Object} option - New option { value, label, description, display_order }
   */
  addOption: async (setting, option) => {
    const updatedValue = [...(setting.setting_value || []), option];
    return await systemSettingsService.update(setting.id, {
      setting_value: updatedValue
    });
  },

  /**
   * Update an option in an array-type setting.
   * @param {Object} setting - Full setting object with id and setting_value
   * @param {string} optionValue - value (code) of the option to update
   * @param {Object} updatedOption - New option fields
   */
  updateOption: async (setting, optionValue, updatedOption) => {
    const updatedValue = (setting.setting_value || []).map(opt =>
      opt.value === optionValue ? { ...opt, ...updatedOption } : opt
    );
    return await systemSettingsService.update(setting.id, {
      setting_value: updatedValue
    });
  },

  /**
   * Delete an option from an array-type setting.
   * @param {Object} setting - Full setting object with id and setting_value
   * @param {string} optionValue - value (code) of the option to remove
   */
  deleteOption: async (setting, optionValue) => {
    const updatedValue = (setting.setting_value || []).filter(opt => opt.value !== optionValue);
    return await systemSettingsService.update(setting.id, {
      setting_value: updatedValue
    });
  }
};

export default systemSettingsService;

